import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { inventoryAPI } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Loader2 } from 'lucide-react';

const fmt = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
const SRC = { route_card_closure: 'Route card closure', production_quarantine: 'Production' };
const STATUS_CLS = { open: 'bg-amber-100 text-amber-800', closed: 'bg-slate-100 text-slate-700' };

const Section = ({ title, children }) => (
  <div>
    <p className="text-xs font-semibold mb-1.5">{title}</p>
    {children}
  </div>
);

const Table = ({ headers, children, testid }) => (
  <table className="w-full text-xs border rounded" data-testid={testid}>
    <thead className="bg-muted/60"><tr>{headers.map(h => <th key={h} className="text-left p-1.5 font-medium">{h}</th>)}</tr></thead>
    <tbody>{children}</tbody>
  </table>
);

const Empty = ({ cols }) => <tr><td colSpan={cols} className="p-2 text-muted-foreground">None in this period</td></tr>;

export const RejectionItemDrilldown = ({ itemId, from, to, onClose }) => {
  const [d, setD] = useState(null);

  useEffect(() => {
    if (!itemId) return;
    setD(null);
    inventoryAPI.rejectionItemDrilldown(itemId, { from_date: from, to_date: to }).then(r => setD(r.data)).catch(() => setD({ error: true }));
  }, [itemId, from, to]);

  return (
    <Dialog open={!!itemId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="rejection-item-drilldown">
        <DialogHeader>
          <DialogTitle className="text-base">
            {d?.item_unique_id} {d?.item_name} <span className="text-xs font-normal text-muted-foreground">{from} to {to}</span>
          </DialogTitle>
        </DialogHeader>
        {!d ? <Loader2 className="w-5 h-5 animate-spin mx-auto my-8" /> : d.error ? <p className="text-sm text-red-600">Could not load details</p> : (
          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <Section title="Route cards">
                <Table headers={['Route card', 'Scrapped', 'MRB rejected', 'Reworked']} testid="drill-route-cards">
                  {d.route_cards.length === 0 ? <Empty cols={4} /> : d.route_cards.map(r => (
                    <tr key={r.route_card_number} className="border-t">
                      <td className="p-1.5 font-mono">{r.route_card_number}</td>
                      <td className="p-1.5 font-mono">{fmt(r.scrapped)}</td>
                      <td className="p-1.5 font-mono text-red-700">{fmt(r.mrb_rejected)}</td>
                      <td className="p-1.5 font-mono text-orange-700">{fmt(r.reworked)}</td>
                    </tr>))}
                </Table>
              </Section>
              <Section title="Reasons">
                <Table headers={['Reason', 'Qty', 'Share']} testid="drill-reasons">
                  {d.by_reason.length === 0 ? <Empty cols={3} /> : d.by_reason.map(r => (
                    <tr key={r.reason} className="border-t">
                      <td className="p-1.5">{r.reason}</td><td className="p-1.5 font-mono">{fmt(r.quantity)}</td>
                      <td className="p-1.5 font-mono">{r.share_pct}%</td>
                    </tr>))}
                </Table>
              </Section>
            </div>
            <Section title={`MRB cases (${d.mrb_cases.length})`}>
              <Table headers={['Opened', 'Source', 'Route card', 'Qty', 'Reason', 'Status', 'Decision', 'Rework WO']} testid="drill-mrb-cases">
                {d.mrb_cases.length === 0 ? <Empty cols={8} /> : d.mrb_cases.map(c => (
                  <tr key={c.id} className="border-t">
                    <td className="p-1.5 font-mono">
                      <Link to={`/quality/mrb?case=${c.id}`} className="text-blue-700 underline" data-testid={`drill-mrb-link-${c.id}`}>
                        {(c.created_at || '').slice(0, 10)}</Link>
                    </td>
                    <td className="p-1.5">{SRC[c.source] || c.source}</td>
                    <td className="p-1.5 font-mono">{c.route_card_number || c.challan_number || '-'}</td>
                    <td className="p-1.5 font-mono">{fmt(c.quantity)}</td>
                    <td className="p-1.5">{c.rejection_reason || '-'}</td>
                    <td className="p-1.5"><Badge className={`text-[10px] ${STATUS_CLS[c.status] || ''}`}>{c.status}</Badge></td>
                    <td className="p-1.5 capitalize">{c.disposition || '-'}</td>
                    <td className="p-1.5 font-mono">{c.rework_wo_number ? `${c.rework_wo_number} (${c.rework_route_card_number})` : '-'}</td>
                  </tr>))}
              </Table>
            </Section>
            <Section title={`Entries (${d.records.length})`}>
              <Table headers={['Date', 'Type', 'Route card', 'Qty', 'Reason', 'By']} testid="drill-records">
                {d.records.length === 0 ? <Empty cols={6} /> : d.records.map(r => (
                  <tr key={r.id} className="border-t">
                    <td className="p-1.5 font-mono">{r.date}</td>
                    <td className="p-1.5">{{ closure_scrap: 'Scrap at closure', mrb_reject: 'MRB reject', rework: 'Rework' }[r.type]}</td>
                    <td className="p-1.5 font-mono">{r.route_card_number || '-'}</td>
                    <td className="p-1.5 font-mono">{fmt(r.quantity)}</td>
                    <td className="p-1.5">{r.reason}</td>
                    <td className="p-1.5 text-muted-foreground">{r.created_by_name}</td>
                  </tr>))}
              </Table>
            </Section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
